import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../Components/Layout';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Breadcrumb from '../Component/Breadcrumb';
import Sidebar from '../../Dashboard/Component/Siderbar';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { subAgencyValidationSchema } from './validationSchema';
import { editSubAgencyValidationSchema } from './validationSchemaEdit';
import config from '../../../config';
import Select from 'react-select';

export default function Edit() {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const [countries, setCountries] = useState([]);
    const userData = JSON.parse(localStorage.getItem('user_data'));
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [addressStates, setAddressStates] = useState([]);
    const [addressCities, setAddressCities] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [agencyId, setAgencyId] = useState(null);
    const [initialValues, setInitialValues] = useState({
        agency: {
            name: "",
            phone: "",
            email: "",
            image: "",
            description: "",
            address: {
                address: "",
                country_id: "",
                state_id: "",
                city_id: "",
                zip_code: "",
            },
            country_id: "",
            state_id: "",
            city_id: "",
            details: {
                website: "",
            },
            markup: 0,
        }
    });

    const fetchSubAgencyDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${config.API_BASE_URL_V1}agency/slug-search/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${userData.token}`,
                    },
                }
            );

            const agencyData = response.data.data;
            console.log("API Response:", agencyData); // Debug log
            
            // Store the agency ID
            setAgencyId(agencyData.id);

            // Set initial values with null checks and default values
            setInitialValues({
                agency: {
                    id: agencyData?.id || "",
                    name: agencyData?.name || "",
                    phone: agencyData?.phone || "",
                    email: agencyData?.email || "",
                    image: agencyData?.profile_image || "",
                    description: agencyData?.description || "",
                    agency_code: agencyData?.agency_code || "",
                    address: {
                        address: agencyData?.address?.address || "",
                        country_id: agencyData?.address?.country_id || "",
                        state_id: agencyData?.address?.state_id || "",
                        city_id: agencyData?.address?.city_id || "",
                        zip_code: agencyData?.address?.zip_code || "",
                    },
                    country_id: agencyData?.countryId || "",
                    state_id: agencyData?.stateId || "",
                    city_id: agencyData?.cityId || "",
                    details: {
                        website: agencyData?.details?.website || "",
                    },
                    markup: agencyData?.markup || 0,
                }
            });

            // Set preview image if exists
            if (agencyData?.image) {
                setPreviewImage(agencyData.image);
            }

            // Fetch location data for main address
            if (agencyData?.countryId) {
                fetchStates('main', agencyData.countryId);
            }
            if (agencyData?.stateId) {
                fetchCities('main', agencyData.stateId);
            }

            // Fetch location data for billing address
            if (agencyData?.address?.country_id) {
                fetchStates('address', agencyData.address.country_id);
            }
            if (agencyData?.address?.state_id) {
                fetchCities('address', agencyData.address.state_id);
            }

        } catch (error) {
            console.error("Error fetching sub agency details:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Failed to fetch sub agency details",
            });
            navigate("/dashboard-sub-agency");
        } finally {
            setLoading(false);
        }
    }, [id, userData.token, navigate]);

    const fetchCountries = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}geo/countries`);
            setCountries(response.data);
        } catch (err) {
            console.error('Error fetching countries:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch countries',
            });
        }
    };
  
    const fetchStates = async (type, countryId) => {
        if (!countryId) return;
        try {
            const response = await axios.get(`${config.API_BASE_URL}geo/states/${countryId}`);
            switch (type) {
                case 'address':
                    setAddressStates(response.data);
                    break;
                default:
                    setStates(response.data);
                    break;
            }
        } catch (err) {
            console.error('Error fetching states:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch states',
            });
        }
    };
    
    const fetchCities = async (type, stateId) => {
        if (!stateId) return;
        try {
            const response = await axios.get(`${config.API_BASE_URL}geo/cities/${stateId}`);
            switch (type) {
                case 'address':
                    setAddressCities(response.data);
                    break;
                default:
                    setCities(response.data);
                    break;
            }
        } catch (err) {
            console.error('Error fetching cities:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch cities',
            });
        }
    };

    useEffect(() => {
        fetchCountries();
        fetchSubAgencyDetails();
    }, [id, fetchSubAgencyDetails]);

    const handleImageChange = (event, setFieldValue) => {
        const file = event.currentTarget.files[0];
        if (file) {
            if (file.size > 5000000) { // 5MB
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'File size should not exceed 5MB',
                });
                return;
            }

            if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Please upload a valid image file (JPG, JPEG, PNG)',
                });
                return;
            }

            setFieldValue('agency.image', file);
            const reader = new FileReader();
            reader.onload = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (values, { setSubmitting, setFieldValue }) => {
        setLoading(true);
        try {
            const formData = new FormData();

            console.log("Values:", values);

            // Append all form fields except image
            Object.keys(values.agency).forEach(key => {
                if (key === 'address' || key === 'details') {
                    Object.keys(values.agency[key]).forEach(subKey => {
                        formData.append(`agency[${key}][${subKey}]`, values.agency[key][subKey]);
                    });
                } else if (key !== 'image') {
                    formData.append(`agency[${key}]`, values.agency[key]);
                }
            });

            // Append image only if it's a File object (new image selected)
            if (values.agency.image instanceof File) {
                formData.append('agency[image]', values.agency.image);
            }

            const response = await axios.post(
                `${config.API_BASE_URL_V1}agency/update-sub-agency/${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${userData.token}`,
                    },
                }
            );

            if (response.data.status === "success") {
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    timerProgressBar: true,
                    timer: 1500,
                    icon: "success",
                    title: "Successfully updated!",
                    showConfirmButton: false,
                });

                navigate("/dashboard-sub-agency");
                window.scrollTo(0, 0);
            } else {
                throw new Error(response.data.message || "An error occurred while updating the sub agency");
            }
        } catch (error) {
            console.error("Error updating sub agency:", error);
            
            let errorMessage = "An unexpected error occurred";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                errorMessage = Object.values(errors).flat().join('\n');
            } else if (error.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                icon: "error",
                title: "Error",
                text: errorMessage,
                confirmButtonText: 'OK',
                allowOutsideClick: false
            });

            // Form değerlerini koru
            Object.keys(values).forEach(key => {
                if (typeof values[key] === 'object') {
                    Object.keys(values[key]).forEach(subKey => {
                        if (typeof values[key][subKey] === 'object') {
                            Object.keys(values[key][subKey]).forEach(nestedKey => {
                                setFieldValue(`${key}.${subKey}.${nestedKey}`, values[key][subKey][nestedKey]);
                            });
                        } else {
                            setFieldValue(`${key}.${subKey}`, values[key][subKey]);
                        }
                    });
                } else {
                    setFieldValue(key, values[key]);
                }
            });
            
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    if(loading) {
        return (
            <div id="loader-wrapper">
                <div id="loader">
                    <span className="loader-line"></span>
                </div>
            </div>
        );
    }

    return (
        <Layout>
            <Breadcrumb title="Sub Agency" />
            <div className="content">
                <div className="container">
                    <div className="row">
                        <Sidebar>
                            <div className="row">
                                <div className="col-12">
                                    <Formik
                                        enableReinitialize={true}
                                        initialValues={initialValues}
                                        validationSchema={editSubAgencyValidationSchema}
                                        onSubmit={handleSubmit}
                                    >
                                        {({ values, setFieldValue, isSubmitting, touched, errors }) => (
                                            <Form>
                                                <div className="card shadow-sm border-0 rounded-lg">
                                                    <div className="card-header bg-primary py-3">
                                                        <h5 className="fs-18 mb-0 text-white">Sub Agency Edit</h5>
                                                    </div>
                                                    <div className="card-body p-4">
                                                        <div className="row">
                                                            {/* Agency Image */}
                                                            <div className="mb-4">
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <label className="form-label fw-bold mb-3">Agency Image</label>
                                                                    {previewImage && (
                                                                        <div className="mb-3" style={{ maxWidth: '200px' }}>
                                                                            <img
                                                                                src={previewImage}
                                                                                alt="Preview"
                                                                                className="img-fluid rounded shadow-sm"
                                                                                style={{ maxHeight: '200px', objectFit: 'contain' }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <div className="w-100">
                                                                        <input
                                                                            type="file"
                                                                            className="form-control form-control-lg"
                                                                            name="agency.image"
                                                                            accept="image/*"
                                                                            onChange={(e) => handleImageChange(e, setFieldValue)}
                                                                        />
                                                                        <small className="text-muted d-block mt-2">
                                                                            Supported formats: JPG, JPEG, PNG. Max size: 5MB
                                                                        </small>
                                                                    </div>
                                                                    <ErrorMessage name="agency.image" component="div" className="text-danger mt-2" />
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Agency Information */}
                                                            <div className="card border-0 shadow-sm mb-4">
                                                                <div className="card-header bg-primary py-3">
                                                                    <h6 className="mb-0 text-white">Sub Agency Information</h6>
                                                                </div>
                                                                <div className="card-body p-4">
                                                                    <div className="row g-3">
                                                                        <div className="col-md-6">
                                                                            <label className="form-label fw-bold">Agency Name<span className="text-danger">*</span></label>
                                                                            <Field
                                                                                type="text"
                                                                                className={`form-control form-control-lg ${touched.agency?.name && errors.agency?.name ? 'is-invalid' : ''}`}
                                                                                name="agency.name"
                                                                                placeholder="Enter agency name"
                                                                                old={values.agency.name}
                                                                            />
                                                                            <ErrorMessage name="agency.name" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <label className="form-label fw-bold">Agency Email<span className="text-danger">*</span></label>
                                                                            <Field
                                                                                type="email"
                                                                                className={`form-control form-control-lg ${touched.agency?.email && errors.agency?.email ? 'is-invalid' : ''}`}
                                                                                name="agency.email"
                                                                                placeholder="Enter agency email"
                                                                                old={values.agency.email}
                                                                            />
                                                                            <ErrorMessage name="agency.email" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <label className="form-label fw-bold">Agency Phone<span className="text-danger">*</span></label>
                                                                            <Field
                                                                                type="text"
                                                                                className={`form-control form-control-lg ${touched.agency?.phone && errors.agency?.phone ? 'is-invalid' : ''}`}
                                                                                name="agency.phone"
                                                                                placeholder="Enter agency phone"
                                                                                old={values.agency.phone}
                                                                            />
                                                                            <ErrorMessage name="agency.phone" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <label className="form-label fw-bold">Agency Markup (%)<span className="text-danger">*</span></label>
                                                                            <Field
                                                                                type="number"
                                                                                className={`form-control form-control-lg ${touched.agency?.markup && errors.agency?.markup ? 'is-invalid' : ''}`}
                                                                                name="agency.markup"
                                                                                placeholder="Enter agency markup"
                                                                                old={values.agency.markup}
                                                                            />
                                                                            <ErrorMessage name="agency.markup" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        
                                                                        
                                                                        
                                                                        <div className="col-12">
                                                                            <label className="form-label fw-bold">Description <small className="text-muted">(Optional)</small></label>
                                                                            <Field
                                                                                as="textarea"
                                                                                className={`form-control form-control-lg ${touched.agency?.description && errors.agency?.description ? 'is-invalid' : ''}`}
                                                                                name="agency.description"
                                                                                placeholder="Enter description"
                                                                                rows="4"
                                                                            />
                                                                            <ErrorMessage name="agency.description" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Commercial Address */}
                                                            <div className="card border-0 shadow-sm mb-4">
                                                                <div className="card-header bg-primary py-3">
                                                                    <h6 className="mb-0 text-white">Commercial Address</h6>
                                                                </div>
                                                                <div className="card-body p-4">
                                                                    <div className="row g-3">
                                                                        <div className="col-md-4">
                                                                            <label className="form-label fw-bold">Country<span className="text-danger">*</span></label>
                                                                            <Select
                                                                                options={countries.map(country => ({
                                                                                    value: country.id,
                                                                                    label: country.name
                                                                                }))}
                                                                                value={values.agency.address.country_id ? {
                                                                                    value: Number(values.agency.address.country_id),
                                                                                    label: countries.find(c => c.id === Number(values.agency.address.country_id))?.name
                                                                                } : null}
                                                                                onChange={(selectedOption) => {
                                                                                    const countryId = selectedOption ? selectedOption.value : '';
                                                                                    setFieldValue('agency.address.country_id', countryId);
                                                                                    setFieldValue('agency.address.state_id', '');
                                                                                    setFieldValue('agency.address.city_id', '');
                                                                                    if (countryId) {
                                                                                        fetchStates('address', countryId);
                                                                                    }
                                                                                }}
                                                                                placeholder="Select a country"
                                                                                isClearable
                                                                                isSearchable
                                                                                className={`form-control-lg ${touched.agency?.address?.country_id && errors.agency?.address?.country_id ? 'is-invalid' : ''}`}
                                                                            />
                                                                            <ErrorMessage name="agency.address.country_id" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <label className="form-label fw-bold">State</label>
                                                                            <Select
                                                                                options={addressStates.map(state => ({
                                                                                    value: state.id,
                                                                                    label: state.name
                                                                                }))}
                                                                                value={values.agency.address.state_id ? {
                                                                                    value: Number(values.agency.address.state_id),
                                                                                    label: addressStates.find(s => s.id === Number(values.agency.address.state_id))?.name
                                                                                } : null}
                                                                                onChange={(selectedOption) => {
                                                                                    const stateId = selectedOption ? selectedOption.value : '';
                                                                                    setFieldValue('agency.address.state_id', stateId);
                                                                                    setFieldValue('agency.address.city_id', '');
                                                                                    if (stateId) {
                                                                                        fetchCities('address', stateId);
                                                                                    }
                                                                                }}
                                                                                placeholder="Select a state"
                                                                                isClearable
                                                                                isSearchable
                                                                                isDisabled={!values.agency.address.country_id}
                                                                                className={`form-control-lg ${touched.agency?.address?.state_id && errors.agency?.address?.state_id ? 'is-invalid' : ''}`}
                                                                            />
                                                                            <ErrorMessage name="agency.address.state_id" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <label className="form-label fw-bold">City</label>
                                                                            <Select
                                                                                options={addressCities.map(city => ({
                                                                                    value: city.id,
                                                                                    label: city.name
                                                                                }))}
                                                                                value={values.agency.address.city_id ? {
                                                                                    value: Number(values.agency.address.city_id),
                                                                                    label: addressCities.find(c => c.id === Number(values.agency.address.city_id))?.name
                                                                                } : null}
                                                                                onChange={(selectedOption) => {
                                                                                    setFieldValue('agency.address.city_id', selectedOption ? selectedOption.value : '');
                                                                                }}
                                                                                placeholder="Select a city"
                                                                                isClearable
                                                                                isSearchable
                                                                                isDisabled={!values.agency.address.state_id}
                                                                                className={`form-control-lg ${touched.agency?.address?.city_id && errors.agency?.address?.city_id ? 'is-invalid' : ''}`}
                                                                            />
                                                                            <ErrorMessage name="agency.address.city_id" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        <div className="col-12">
                                                                            <label className="form-label fw-bold">Address<span className="text-danger">*</span></label>
                                                                            <Field
                                                                                type="text"
                                                                                className={`form-control form-control-lg ${touched.agency?.address?.address && errors.agency?.address?.address ? 'is-invalid' : ''}`}
                                                                                name="agency.address.address"
                                                                                placeholder="Enter address"
                                                                                old={values.agency.address.address}
                                                                            />
                                                                            <ErrorMessage name="agency.address.address" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <label className="form-label fw-bold">Zip Code <small className="text-muted">(Optional)</small></label>
                                                                            <Field
                                                                                type="text"
                                                                                className={`form-control form-control-lg ${touched.agency?.address?.zip_code && errors.agency?.address?.zip_code ? 'is-invalid' : ''}`}
                                                                                name="agency.address.zip_code"
                                                                                placeholder="Enter zip code"
                                                                                old={values.agency.address.zip_code}
                                                                            />
                                                                            <ErrorMessage name="agency.address.zip_code" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Details */}
                                                            <div className="card border-0 shadow-sm mb-4">
                                                                <div className="card-header bg-primary py-3">
                                                                    <h6 className="mb-0 text-white">Details</h6>
                                                                </div>
                                                                <div className="card-body p-4">
                                                                    <div className="row g-3">
                                                                        <div className="col-md-6">
                                                                            <label className="form-label fw-bold">Website <small className="text-muted">(Optional)</small></label>
                                                                            <Field
                                                                                type="text"
                                                                                className={`form-control form-control-lg ${touched.agency?.details?.website && errors.agency?.details?.website ? 'is-invalid' : ''}`}
                                                                                name="agency.details.website"
                                                                                placeholder="Enter website"
                                                                                old={values.agency.details.website}
                                                                            />
                                                                            <ErrorMessage name="agency.details.website" component="div" className="text-danger mt-2" />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="text-center mt-4">
                                                            <button 
                                                                type="submit" 
                                                                className="btn btn-primary btn-lg px-5"
                                                                disabled={isSubmitting}
                                                            >
                                                                {isSubmitting ? 'Saving...' : 'Save'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </Sidebar>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
